import React from 'react';
import _ from 'lodash';
import { Column } from 'spw-shared/globalComponents/grid';
import {
  CUSTOM_SUBCATEGORY,
  PRIVATE_OFFICES_SUBCATEGORY,
  WORKSHEET_MAP,
  WORKSTATIONS_SUBCATEGORY,
} from 'spw-constants';
import { compareStrings } from 'spw-shared/utils';
import { IProjectSpaceTypeCategoryOrSubcategory } from 'spw-redux/modules/projects/types';
import { GridRow } from 'spw-programming-projects/building-details/worksheet/styles';
import useUnitConversion from 'spw-components/unit-conversion/hooks';
import { isIWCategoryOrSubcategory } from 'spw-projects/utils';
import { useSelector } from 'react-redux';
import { selectMeasurementSystem } from 'spw-redux/modules/user/selectors';
import { MeasurementSystem } from 'spw-components/unit-conversion/types';
import { GridHeaderColumn } from './styles';
import { TargetRatioTooltip } from './target-ratio-tooltip';

interface ProgrammingCategoryHeaderProperty {
  name: string;
  categoryOrSubcategory?: IProjectSpaceTypeCategoryOrSubcategory;
  isWorksheetOpenForDownload: boolean;
  showDetailedSharedLabels?: boolean;
  isRatio?: boolean;
}

const MacroProgrammingCategoryHeader: React.FC<ProgrammingCategoryHeaderProperty> = ({
  name,
  categoryOrSubcategory,
  isWorksheetOpenForDownload,
  showDetailedSharedLabels = false,
  isRatio,
}) => {
  const { getUnit } = useUnitConversion();
  const measurementSystem = useSelector(selectMeasurementSystem);
  const {
    category: defaultCategoryOrSubcategory,
  } = categoryOrSubcategory.defaultSpaceTypeCategory;

  const defaultCategoryName =
    defaultCategoryOrSubcategory === CUSTOM_SUBCATEGORY
      ? name
      : WORKSHEET_MAP[defaultCategoryOrSubcategory].title;

  const hasCategoryBeenRenamed = !compareStrings(name, defaultCategoryName);
  const label = _.toUpper(defaultCategoryName.split(' ')[0]);

  const isIndividualWorkspaceSubcategory = isIWCategoryOrSubcategory(
    categoryOrSubcategory
  );

  const dimensionsUnit = getUnit('linear', {
    outputIsFractionUnit: measurementSystem === MeasurementSystem.metric,
  });

  return (
    <GridRow className="header">
      <GridHeaderColumn xs={3}>
        {showDetailedSharedLabels && !isIndividualWorkspaceSubcategory && (
          <TargetRatioTooltip />
        )}
      </GridHeaderColumn>
      <GridHeaderColumn xs={1} style={{ paddingRight: '16px' }}>
        {isRatio ? (
          <Column>
            <div>TARGET</div>
            <div>RATIO</div>
          </Column>
        ) : (
          <Column>
            <div>SEAT</div>
            <div>% MIX</div>
          </Column>
        )}
      </GridHeaderColumn>
      <GridHeaderColumn xs={1} style={{ paddingRight: '16px' }}>
        QTY
      </GridHeaderColumn>
      <GridHeaderColumn xs={1}>
        <Column>
          {!isWorksheetOpenForDownload && window.SPWExport ? (
            <></>
          ) : (
            <>
              <div>SEAT</div>
              <div>CAPACITY</div>
            </>
          )}
        </Column>
      </GridHeaderColumn>
      <GridHeaderColumn xs={1} style={{ paddingRight: '8px' }}>
        <Column>
          <div>
            {(!hasCategoryBeenRenamed || isIndividualWorkspaceSubcategory) && (
              <>{isIndividualWorkspaceSubcategory ? 'WORK' : label}</>
            )}
          </div>
          <div>SEATS</div>
        </Column>
      </GridHeaderColumn>
      <GridHeaderColumn xs={2} className="seat-percent-column">
        <Column>
          {hasCategoryBeenRenamed ||
          defaultCategoryOrSubcategory === CUSTOM_SUBCATEGORY ? (
            <span>% OF SEATS</span>
          ) : (
            <>
              <span>
                % OF{' '}
                {defaultCategoryOrSubcategory === WORKSTATIONS_SUBCATEGORY
                  ? 'WORKSTATION'
                  : label}
              </span>
              <span style={{ whiteSpace: 'nowrap' }}>
                {defaultCategoryOrSubcategory === PRIVATE_OFFICES_SUBCATEGORY
                  ? 'OFFICE'
                  : ''}{' '}
                SEATS
              </span>
            </>
          )}
        </Column>
      </GridHeaderColumn>
      <GridHeaderColumn xs={2} className="justify-start">
        DIMENSIONS (
        {measurementSystem === MeasurementSystem.metric
          ? dimensionsUnit
          : dimensionsUnit.toUpperCase()}
        )
      </GridHeaderColumn>
      <GridHeaderColumn xs={1}>
        {!isWorksheetOpenForDownload && window.SPWExport ? '' : getUnit('area')}
      </GridHeaderColumn>
      <GridHeaderColumn xs={1} style={{ paddingRight: '8px' }}>
        TOTAL {getUnit('area')}
      </GridHeaderColumn>
    </GridRow>
  );
};

export default MacroProgrammingCategoryHeader;
